/**
 * /zh/content-roadmap — 内容路线图中文 SEO 页。与 ContentRoadmap.tsx
 * 同构，原生中文，hreflang 双向。受众主要是内容营销，温和地转向
 * Draftlize 的决策层定位，不假装是内容营销工具。
 */
import { MarketingShell } from './MarketingShell';

export function ContentRoadmapZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/content-roadmap"
      documentTitle="内容路线图是什么？模板与示例 · Draftlize"
      documentDescription="内容路线图（content roadmap）规划你要发什么、何时发、为什么发——绑定目标与受众。讲清它是什么、怎么搭一份，以及怎么让它不漂移。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 内容路线图</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            内容路线图是什么？<br/>
            <em>以及怎么让它保持诚实。</em>
          </h1>
          <p className="lede">
            <em>内容路线图</em>（content roadmap）是一份关于你要发布什么、按什么顺序、以及——把路线图和日历区分开的那一点——<em>为什么</em>发的计划：每一篇服务于哪个受众、哪个目标、漏斗的哪个阶段。它让内容团队对齐方向，而不是闷头产出、碰运气。这页把内容路线图到底是什么、它和日历有何不同、怎么搭一份讲清楚，然后是诚实的那部分：路线图只和它底下的策略一样靠谱，而当策略一变，路线图就悄悄对不上了。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>内容路线图到底是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            内容路线图是你的内容策略（「我们要赢得 AI 原生 PM 的自然搜索」）和日常日历（「周二发那篇 PRD 指南」）之间的桥。它把计划中的内容按主题或支柱（pillar）分组，绑定到一个目标和一个受众，并沿时间排列。重点不是那串标题——而是每一篇都能回答「这是给谁的、它该起什么作用」，从而让你不再为了填一个发布位而发。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            它不是内容<em>日历</em>（那是「什么时候发什么」的带日期排期），也不是单篇的编辑计划。路线图站在两者之上：它是被具体到能拿来排优先级的策略，又松到下个季度的主题能随着你摸清什么有共鸣而灵活调整。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">别搞混</span>
            <h2 className="serif">路线图 vs 日历 <em>vs 编辑计划。</em></h2>
          </div>
          <p>三层都被叫作「内容计划」。把它们分开，才能让你的日历不沦为一台没有方向的跑步机。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>构件</th>
              <th>回答什么</th>
              <th>时间尺度</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>内容路线图</td><td className="yes">什么主题、为什么，按受众/目标</td><td>季度，宽松</td></tr>
            <tr><td>内容日历</td><td className="partial">哪天发什么</td><td>周，明确</td></tr>
            <tr><td>编辑计划</td><td className="partial">单篇怎么做出来</td><td>每篇</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">怎么搭一份，<em>五步走。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">从目标和受众出发</h3>
            <p>点明内容是为了什么——自然增长、产品激活、思想领导力——以及你到底在为谁写。下游的一切都是服务于此的赌注。跳过它，你得到的是一张没有主线的话题日历。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">定义主题 / 支柱</h3>
            <p>把内容归到几个耐用的主题（「PRD」「决策追踪」「AI 辅助 PM」），而不是一张扁平的标题清单。主题让具体篇目可以变、而路线图不算变，并且能干净地对应到搜索词簇。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">给篇目排优先级</h3>
            <p>按触达、业务影响、成本给候选篇目打分，让顺序反映价值，而不是谁吆喝得响。一个 <a href="/zh/prioritization-frameworks" style={{ color: 'var(--amber)' }}>优先级框架</a> 对内容和对产品一样好使。</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">沿时间尺度排序</h3>
            <p>把主题铺到各季度——当季做实，之后做成方向。把已承诺的近期篇目交给内容日历；让路线图停在主题的高度。</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">边学边回看</h3>
            <p>什么有共鸣会改变赌注。难的是：当策略一变，路线图、以及每一篇以旧计划为由的篇目都该跟着变——而电子表格里没有任何东西告诉你是哪些。这就是下一节。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">路线图有多「活」，全看<em>它底下的决策。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            诚实地说：Draftlize 不是内容营销工具——做日历和发布，用为此而造的工具。但内容路线图和产品路线图一样，压在一些决策上（受众、目标、定位），其中一个一变，路线图就悄悄对不上了。那一层决策，正是 Draftlize 持有的。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">赌注变成卡片</h3>
            <p>目标受众、目标、每篇所倚靠的定位角度——在 Draftlize 里这些是可寻址卡片，而不是埋在某份规划文档里的假设。一篇计划中的内容能指向它所服务的那个确切赌注。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">赌注一变，依赖它的标 stale</h3>
            <p>你重新切分了受众，或定位变了。所有倚靠旧赌注的路线图主题和计划篇目自动标记 stale——于是策略改动会传播到计划里，而不是让你照着一份已不成立的 brief 继续发。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 基于「活」计划起草</h3>
            <p>当 agent 经 MCP 帮你列下一篇的提纲，它读到的是当前的受众和目标——不是上个季度的。路线图始终是你的内容真正据以构建的那个真相源。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          内容路线图失败，不是因为日历跳了票，而是因为策略动了，计划没跟着动。
        </blockquote>
        <cite>留着你的日历和发布工具，持有路线图所依赖的那些决策。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '内容路线图和内容日历有什么区别？', a: '内容路线图是策略性的——它把计划内容按主题分组、绑定目标与受众，用宽松的时间段；内容日历是执行性的——它说清哪天发什么。路线图解释为什么、大致何时，日历钉死日期。两者都要，且按这个顺序。' },
            { q: '内容路线图和产品路线图有何不同？', a: '同样的形状，不同的主题。产品路线图规划你要做什么，内容路线图规划你要发什么。两者都把工作组织成绑定目标的主题，都该避免假装精确日期，也都会在底下的策略变化时悄悄漂移。' },
            { q: '内容路线图归谁负责？', a: '通常是内容负责人或市场负责人，结合 SEO、产品营销、销售对「受众需要什么」的输入一起搭。负责，意味着让它绑定目标、保持最新——而不是亲自写每一篇。' },
            { q: '做内容路线图需要什么工具？', a: '就路线图和日历本身而言，一个专门的内容工具、一张电子表格，或 Notion、Asana 这类项目工具都行。Draftlize 不是其中之一——它坐在底下，持有路线图所依赖的受众与策略决策，让它们不悄悄过时。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">持有路线图底下的决策<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            在你已有的地方规划和发布。补上 Draftlize 来做那些工具都没有的一层：让内容路线图所依赖的受众、目标、定位保持最新——这样计划和策略永远不会悄悄打架。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
